import React, { useState, useEffect, useMemo, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import {
  ShoppingBag,
  FileText,
  Clock,
  AlertCircle,
  CreditCard,
  TrendingUp,
  Package,
  ChevronRight,
} from "lucide-react";
import Button from "../components/ui/Button";
const BarChart = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.BarChart })),
);
const Bar = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.Bar })),
);
const XAxis = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.XAxis })),
);
const YAxis = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.YAxis })),
);
const CartesianGrid = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.CartesianGrid })),
);
const Tooltip = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.Tooltip })),
);
const ResponsiveContainer = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.ResponsiveContainer })),
);
const PieChart = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.PieChart })),
);
const Pie = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.Pie })),
);
const Cell = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.Cell })),
);
const Legend = React.lazy(() =>
  import("recharts").then((m) => ({ default: m.Legend })),
);

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingApprovals: 0,
    draftOrders: 0,
    recentOrders: [],
    recentInvoices: [],
    outstandingBalance: 0,
    creditLimit: 0,
  });

  // Chart Data States
  const [spendingDataRaw, setSpendingDataRaw] = useState([]);
  const [orderStatusDataRaw, setOrderStatusDataRaw] = useState([]);

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [drafts, approvals, orders, invoices, profile] =
          await Promise.all([
            apiService.getDraftOrders(),
            apiService.getPendingApprovals(),
            apiService.getOrders({ limit: 10 }),
            apiService.getInvoices({ limit: 5 }),
            apiService.getCustomerProfile(),
          ]);

        const recentOrders = orders.orders || [];

        setStats({
          pendingApprovals: approvals.length,
          draftOrders: drafts.length,
          recentOrders: recentOrders.slice(0, 5),
          recentInvoices: invoices.invoices || [],
          outstandingBalance: profile.outstandingBalance || 0,
          creditLimit: profile.creditLimit || 0,
        });

        setSpendingDataRaw(recentOrders.slice(0, 7).reverse());
        setOrderStatusDataRaw(recentOrders);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  // Memoize chart data
  const spendingData = useMemo(
    () =>
      spendingDataRaw.map((order) => ({
        name: new Date(order.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        amount: order.totalAmount,
      })),
    [spendingDataRaw],
  );

  const orderStatusData = useMemo(() => {
    const statusCounts = orderStatusDataRaw.reduce((acc, order) => {
      const status = order.status || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    return Object.keys(statusCounts).map((status) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: statusCounts[status],
    }));
  }, [orderStatusDataRaw]);

  const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 ${color} rounded-lg`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {value}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Welcome back, {user?.name}! Here's your account overview.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate("/customer/products")}
            className="flex items-center gap-2"
          >
            <Package className="w-4 h-4" />
            Create New Order
          </Button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Pending Approvals"
          value={stats.pendingApprovals}
          icon={Clock}
          color="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
          onClick={() => navigate("/customer/products")}
        />
        <StatCard
          title="Draft Orders"
          value={stats.draftOrders}
          icon={FileText}
          color="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
          onClick={() => navigate("/customer/products")}
        />
        <StatCard
          title="Outstanding Balance"
          value={`₹${stats.outstandingBalance.toLocaleString()}`}
          icon={CreditCard}
          color="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400"
          onClick={() => navigate("/customer/payables")}
        />
        <StatCard
          title="Available Credit"
          value={`₹${(
            stats.creditLimit - stats.outstandingBalance
          ).toLocaleString()}`}
          icon={TrendingUp}
          color="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
          onClick={() => navigate("/customer/profile")}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spending Overview Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Spending Overview
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Recent order trends
                </p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <Suspense fallback={<LoadingSpinner size="md" />}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={spendingData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    opacity={0.1}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#6B7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6B7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `₹${value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      border: "none",
                      borderRadius: "8px",
                      color: "#F3F4F6",
                    }}
                    itemStyle={{ color: "#F3F4F6" }}
                    formatter={(value) => [
                      `₹${value.toLocaleString()}`,
                      "Amount",
                    ]}
                  />
                  <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Suspense>
          </div>
        </div>

        {/* Order Status Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Order Status Distribution
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Current order breakdown
                </p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <Suspense fallback={<LoadingSpinner size="md" />}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      border: "none",
                      borderRadius: "8px",
                      color: "#F3F4F6",
                    }}
                    itemStyle={{ color: "#F3F4F6" }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Suspense>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <ShoppingBag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Recent Orders
              </h3>
            </div>
            <a
              href="/customer/orders"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {stats.recentOrders.length > 0 ? (
                  stats.recentOrders.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => navigate(`/customer/orders`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white truncate max-w-[8rem]">
                        {order.poNumber || order.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 truncate max-w-[8rem]">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap truncate max-w-[8rem]">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${
                            order.status === "delivered"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : order.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium truncate max-w-[8rem]">
                        ₹{order.totalAmount?.toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No recent orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                <CreditCard className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Recent Invoices
              </h3>
            </div>
            <a
              href="/customer/invoices"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {stats.recentInvoices.length > 0 ? (
                  stats.recentInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => navigate(`/customer/invoices`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${
                            invoice.status === "paid"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : invoice.status === "overdue"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium">
                        ₹{invoice.totalAmount?.toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No recent invoices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
